import { CompanyRepository, SettingsRepository } from '@backend/repository'
import { removeNulls } from '@core/utils'
import { InjectQueue } from '@nestjs/bullmq'
import { Injectable, Logger } from '@nestjs/common'
import { minutesToMilliseconds } from 'date-fns'
import { In, Raw } from 'typeorm'
import {
	GOOGLE_RESERVATIONS_QUEUES,
	UploadAvailabilityFeedQueue,
	UploadMerchantFeedQueue,
	UploadServicesFeedQueue,
} from '../../../../../core'
import { GOOGLE } from '../../../../../config'
import { Cron } from '../../../../../base'

@Injectable()
export class UploadFeedsCron {
	private readonly logger = new Logger(UploadFeedsCron.name)

	constructor(
		private readonly _companyRepository: CompanyRepository,
		private readonly _settingsRepository: SettingsRepository,
		@InjectQueue(GOOGLE_RESERVATIONS_QUEUES.UPLOAD_MERCHANT_FEED)
		private readonly _uploadMerchantFeedQueue: UploadMerchantFeedQueue,
		@InjectQueue(GOOGLE_RESERVATIONS_QUEUES.UPLOAD_SERVICES_FEED)
		private readonly _uploadServicesFeedQueue: UploadServicesFeedQueue,
		@InjectQueue(GOOGLE_RESERVATIONS_QUEUES.UPLOAD_AVAILABILITY_FEED)
		private readonly _uploadAvailabilityFeedQueue: UploadAvailabilityFeedQueue,
	) {}

	@Cron({
		name: 'google_feeds_upload',
		schedule: '0 0 4 * * *',
		appconfigScheduleKey: 'google_feeds_upload_cron_schedule',
	})
	async uploadFeeds() {
		try {
			const restaurantIds = await this.getRestaurants()

			if (restaurantIds.length === 0) {
				this.logger.log('No restaurants found with Google Reservations enabled, skipping feed upload')
				return
			}

			await this.queueFeedUploads(restaurantIds)
		} catch (error) {
			this.logger.error({ message: 'Cron Job to upload Google Reservation Feeds failed', error })
		}
	}

	private async getRestaurants(): Promise<string[]> {
		const settings = await this._settingsRepository.find({
			where: {
				reservation: Raw((alias) => `${alias} -> 'googleReservations' ->> 'enabled' = 'true'`),
			},
		})

		if (settings.length === 0) return []

		const restaurantIds = settings.map((setting) => setting.tenantId).filter(removeNulls)

		const restaurants = await this._companyRepository.find({
			where: { id: In(restaurantIds) },
			select: { name: true },
		})

		this.logger.log({
			message: 'Restaurants with Google reservations enabled',
			restaurants: restaurants.map((restaurant) => restaurant.name),
			count: restaurants.length,
		})

		return restaurantIds
	}

	private async queueFeedUploads(restaurantIds: string[]): Promise<void> {
		try {
			await this._uploadMerchantFeedQueue.add(
				'upload_merchant_feed',
				{ restaurantIds },
				{
					attempts: 3,
					backoff: {
						type: 'exponential',
						delay: 10_000 + Math.floor(Math.random() * 60_000),
					},
				},
			)

			await this._uploadServicesFeedQueue.add(
				'upload_services_feed',
				{ restaurantIds },
				{
					delay: minutesToMilliseconds(1),
					attempts: 3,
					backoff: {
						type: 'exponential',
						delay: 10_000 + Math.floor(Math.random() * 60_000),
					},
				},
			)

			await this._uploadAvailabilityFeedQueue.add(
				'upload_availability_feed',
				{ restaurantIds },
				{
					delay: minutesToMilliseconds(2),
					attempts: 3,
					backoff: {
						type: 'exponential',
						delay: 10_000 + Math.floor(Math.random() * 60_000),
					},
				},
			)

			this.logger.log({ message: 'Queued feed uploads for restaurants', restaurantIds })
		} catch (error) {
			this.logger.error({ message: 'Failed to queue feed uploads', error })
			throw error
		}
	}
}
